/**
 * Manage company site page list list items
 *
 * @module listItems
 */
import Ajax from '@/lib/ajax_wrapper'
import {authHeader, request} from '@/lib/request'

/**
 * Generate path to list item from api url base
 *
 * @private
 *
 * @param {string} companyId
 * @param {string} siteId
 * @param {string} pageId
 * @param {string} listId
 *
 * @returns {string} - path to company site page list list item
 */
function listItemsPath(companyId, siteId, pageId, listId) {
  return `/admin/companies/${companyId}/sites/${siteId}/pages/${pageId}/lists/${listId}/list_items`
}

function listItemsImagesPath(siteId, pageId, listId, listItemId) {
  return `/admin/sites/${siteId}/pages/${pageId}/lists/${listId}/list_items/${listItemId}/image`
}

/**
 * Read paginated company site page list list items
 *
 * @param {string} companyId
 * @param {string} siteId
 * @param {string} pageId
 * @param {string} listId
 * @param {object} pagination
 * @param {integer} pagination.number
 * @param {integer} pagination.perPage
 *
 * @returns {promise}
 */
export function readCompanySitePageListListItemsData(
  companyId,
  siteId,
  pageId,
  listId,
  pagination
) {
  return Ajax.request({
    url: listItemsPath(companyId, siteId, pageId, listId),
    page: pagination
  })
}

/**
 * Create a company site page list list item
 *
 * @param {string} companyId
 * @param {string} siteId
 * @param {string} pageId
 * @param {string} listId
 * @param {object} listItem
 * @param {string} listItem.key
 * @param {string} listItem.value
 * @param {string} listItem.weight
 *
 * @returns {promise}
 */
export function createCompanySitePageListListItemData(
  companyId,
  siteId,
  pageId,
  listId,
  listItem
) {
  return Ajax.request({
    url: listItemsPath(companyId, siteId, pageId, listId),
    method: 'POST',
    data: {
      list_item: listItem
    }
  })
}

/**
 * Read a company site page list list item
 *
 * @param {string} companyId
 * @param {string} siteId
 * @param {string} pageId
 * @param {string} listId
 * @param {string} id
 *
 * @returns {promise}
 */
export function readCompanySitePageListListItemData(
  companyId,
  siteId,
  pageId,
  listId,
  id
) {
  return Ajax.request({
    url: `${listItemsPath(companyId, siteId, pageId, listId)}/${id}`
  })
}

/**
 * Update a company site page list list item
 *
 * @param {string} companyId
 * @param {string} siteId
 * @param {string} pageId
 * @param {string} listId
 * @param {string} id
 * @param {object} listItem
 * @param {string} listItem.key
 * @param {string} listItem.value
 * @param {string} listItem.weight
 *
 * @returns {promise}
 */
export function updateCompanySitePageListListItemData(
  companyId,
  siteId,
  pageId,
  listId,
  listItemId,
  listItem
) {
  return Ajax.request({
    url: `${listItemsPath(companyId, siteId, pageId, listId)}/${listItemId}`,
    method: 'PATCH',
    data: {
      list_item: listItem
    }
  })
}

/**
 * Delete a company site page list list item
 *
 * @param {string} companyId
 * @param {string} siteId
 * @param {string} pageId
 * @param {string} listId
 * @param {string} id
 *
 * @returns {promise}
 */
export function deleteCompanySitePageListListItemData(
  companyId,
  siteId,
  pageId,
  listId,
  id
) {
  return Ajax.request({
    url: `${listItemsPath(companyId, siteId, pageId, listId)}/${id}`,
    method: 'DELETE'
  })
}

export function updateCompanySitePageListListItemImage(
  companyId,
  siteId,
  pageId,
  listId,
  listItemId,
  data,
  token
) {
  const method = 'PATCH'
  const url = `${listItemsImagesPath(siteId, pageId, listId, listItemId)}`
  const headers = authHeader(token)

  return request({data, headers, method, url})
    .then((xhr) => {
      return JSON.parse(xhr.responseText)
    })
    .catch((xhr) => {
      return JSON.parse(xhr.responseText)
    })
}

/**
 * Delete a list item image
 *
 * @param {string} companyId
 * @param {string} id
 *
 * @returns {promise}
 */
export function deleteCompanySitePageListListItemImage(
  companyId,
  siteId,
  pageId,
  listId,
  listItemId
) {
  return Ajax.request({
    url: `${listItemsImagesPath(siteId, pageId, listId, listItemId)}`,
    method: 'DELETE'
  })
}
